/**
 * Gemeinsame Ansicht für die Rezeptübersicht und die Suche.
 *
 * Beide Bereiche nutzen dieselbe Liste, dieselben Filter und dieselbe
 * Sortierung. Sie unterscheiden sich nur in Kopfzeile und Vorauswahl.
 */
import { useMemo, useState } from 'react';
import type { RecipeSummary } from '../api/types.ts';
import { AppIcon } from './AppIcon.tsx';
import { FilterChip, QuickChip, difficultyLabel } from './Chips.tsx';
import { FilterPanel } from './FilterPanel.tsx';
import { RecipeCard } from './RecipeCard.tsx';
import { applyFilter } from '../lib/filter.ts';
import { hasActiveFilters } from '../lib/search.ts';
import type { RecipeFilter } from '../lib/search.ts';
import { useCatalog, useIngredientIndex, useSearchIndex } from '../state/catalog.ts';
import { useRecipeFilter } from '../state/filterParams.ts';
import { texts } from '../texts/de.ts';

/** Eigenschaften des Browsers. */
export interface RecipeBrowserProps {
  /** Die Übersicht zeigt zusätzlich Kategorien und beliebte Schlagwörter. */
  readonly mode: 'list' | 'search' | 'favorites';
  readonly title: string;
  readonly subtitle: string;
}

/** Wie viele Tage gelten als "lange nicht gekocht"? */
const LONG_AGO_DAYS = 21;

/** Rezeptliste mit Suche, Filtern und Sortierung. */
export function RecipeBrowser({ mode, title, subtitle }: RecipeBrowserProps) {
  const catalog = useCatalog();
  const { filter, setFilter, reset } = useRecipeFilter();
  const [filterOpen, setFilterOpen] = useState(false);
  const [listView, setListView] = useState(false);

  const searchIndex = useSearchIndex(catalog.recipes);
  const needsIngredients = filter.withIngredients.length > 0 || filter.withoutIngredients.length > 0;
  const ingredientIndex = useIngredientIndex(catalog.recipes, needsIngredients);

  const results = useMemo(
    () => applyFilter(catalog.recipes, filter, searchIndex, ingredientIndex),
    [catalog.recipes, filter, searchIndex, ingredientIndex],
  );

  const longAgoDate = new Date(Date.now() - LONG_AGO_DAYS * 24 * 60 * 60 * 1000).toISOString();

  const quick = {
    fast: filter.maxTotalMinutes === 30,
    easy: filter.difficulties.length === 1 && filter.difficulties[0] === 'easy',
    top: filter.minRating === 4 && !filter.ratingByMe,
    lastCooked: filter.sort === 'lastCooked' && filter.cookedBefore === null,
    longAgo: filter.cookedBefore !== null,
  };

  const showHighlights = mode === 'list' && !hasActiveFilters(filter) && filter.query === '';
  const popularTags = [...catalog.tags].sort((a, b) => b.recipeCount - a.recipeCount).slice(0, 12);
  const categoryName = (id: string) => catalog.categories.find((entry) => entry.id === id)?.name ?? id;

  return (
    <section className="mx-auto max-w-5xl px-4 py-6">
      <header className="mb-4 flex items-start justify-between gap-3">
        <div>
          <h1 className="text-2xl font-bold tracking-tight">{title}</h1>
          <p className="mt-0.5 text-sm text-content-muted">{subtitle}</p>
        </div>
        <button
          type="button"
          onClick={() => setListView((value) => !value)}
          aria-label={listView ? texts.recipes.gridView : texts.recipes.listView}
          className="rounded-xl border border-border-subtle p-2 text-content-muted"
        >
          <AppIcon name="menu" size={18} />
        </button>
      </header>

      <div className="mb-3 flex gap-2">
        <div className="relative flex-1">
          <AppIcon
            name="search"
            size={18}
            className="pointer-events-none absolute top-1/2 left-3 -translate-y-1/2 text-content-muted"
          />
          <input
            value={filter.query}
            onChange={(event) => setFilter({ ...filter, query: event.target.value })}
            placeholder={texts.recipes.searchPlaceholder}
            className="w-full rounded-xl border border-border-subtle bg-surface-raised py-2.5 pr-3 pl-10 outline-none focus:border-accent"
          />
        </div>
        <button
          type="button"
          onClick={() => setFilterOpen(true)}
          className="flex items-center gap-2 rounded-xl border border-border-subtle bg-surface-raised px-3 text-sm"
        >
          <AppIcon name="sliders" size={18} />
          {texts.filter.title}
          {hasActiveFilters(filter) ? (
            <span className="rounded-full bg-accent px-1.5 text-xs text-on-accent">•</span>
          ) : null}
        </button>
      </div>

      <div className="mb-4 flex gap-2 overflow-x-auto pb-1">
        <QuickChip
          label={texts.recipes.quickFast}
          active={quick.fast}
          onClick={() => setFilter({ ...filter, maxTotalMinutes: quick.fast ? 0 : 30 })}
        />
        <QuickChip
          label={texts.recipes.quickEasy}
          active={quick.easy}
          onClick={() => setFilter({ ...filter, difficulties: quick.easy ? [] : ['easy'] })}
        />
        <QuickChip
          label={texts.recipes.quickTop}
          active={quick.top}
          onClick={() => setFilter({ ...filter, minRating: quick.top ? 0 : 4, ratingByMe: false })}
        />
        <QuickChip
          label={texts.recipes.quickLastCooked}
          active={quick.lastCooked}
          onClick={() => setFilter({ ...filter, sort: 'lastCooked', cookedBefore: null })}
        />
        <QuickChip
          label={texts.favorites.only}
          active={filter.favoritesOnly}
          onClick={() => setFilter({ ...filter, favoritesOnly: !filter.favoritesOnly })}
        />
        <QuickChip
          label={texts.recipes.quickLongAgo}
          active={quick.longAgo}
          onClick={() =>
            setFilter({
              ...filter,
              sort: 'lastCooked',
              cookedBefore: quick.longAgo ? null : longAgoDate,
            })
          }
        />
      </div>

      {showHighlights && catalog.categories.length > 0 ? (
        <div className="mb-6">
          <h2 className="mb-2 text-sm font-semibold text-content-muted">{texts.recipes.categories}</h2>
          <div className="grid grid-cols-2 gap-2 sm:grid-cols-3 lg:grid-cols-4">
            {catalog.categories.map((category) => (
              <button
                key={category.id}
                type="button"
                onClick={() => setFilter({ ...filter, categoryIds: [category.id] })}
                className="flex items-center gap-3 rounded-card border border-border-subtle bg-surface-raised p-3 text-left transition-colors hover:border-accent"
              >
                <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-accent-soft text-accent-strong">
                  <AppIcon name="bowl" size={18} />
                </span>
                <span className="min-w-0">
                  <span className="block truncate text-sm font-medium">{category.name}</span>
                  <span className="text-xs text-content-muted">{category.recipeCount}</span>
                </span>
              </button>
            ))}
          </div>
        </div>
      ) : null}

      {showHighlights && popularTags.length > 0 ? (
        <div className="mb-6">
          <h2 className="mb-2 text-sm font-semibold text-content-muted">{texts.recipes.popularTags}</h2>
          <div className="flex flex-wrap gap-2">
            {popularTags.map((tag) => (
              <button
                key={tag.id}
                type="button"
                onClick={() => setFilter({ ...filter, tagNames: [tag.name] })}
                className="rounded-full bg-surface-sunken px-3 py-1 text-xs text-content-muted hover:text-content"
              >
                {tag.name} · {tag.recipeCount}
              </button>
            ))}
          </div>
        </div>
      ) : null}

      <div className="mb-3 flex flex-wrap items-center justify-between gap-2">
        <p className="text-sm text-content-muted">
          {results.length} {texts.recipes.results}
        </p>
        <label className="flex items-center gap-2 text-sm text-content-muted">
          {texts.recipes.sort}
          <select
            value={filter.sort}
            onChange={(event) => setFilter({ ...filter, sort: event.target.value as RecipeFilter['sort'] })}
            className="rounded-xl border border-border-subtle bg-surface-raised px-2 py-1.5 text-sm"
          >
            <option value="newest">{texts.recipes.sortNewest}</option>
            <option value="rating">{texts.recipes.sortRating}</option>
            <option value="totalTime">{texts.recipes.sortTotalTime}</option>
            <option value="activeTime">{texts.recipes.sortActiveTime}</option>
            <option value="lastCooked">{texts.recipes.sortLastCooked}</option>
            <option value="title">{texts.recipes.sortTitle}</option>
          </select>
        </label>
      </div>

      {hasActiveFilters(filter) || filter.query !== '' ? (
        <div className="mb-3 flex flex-wrap items-center gap-2">
          {filter.query === '' ? null : (
            <FilterChip label={`„${filter.query}“`} onRemove={() => setFilter({ ...filter, query: '' })} />
          )}
          {filter.categoryIds.map((id) => (
            <FilterChip
              key={id}
              label={categoryName(id)}
              onRemove={() =>
                setFilter({ ...filter, categoryIds: filter.categoryIds.filter((entry) => entry !== id) })
              }
            />
          ))}
          {filter.tagNames.map((name) => (
            <FilterChip
              key={name}
              label={name}
              onRemove={() =>
                setFilter({ ...filter, tagNames: filter.tagNames.filter((entry) => entry !== name) })
              }
            />
          ))}
          {filter.difficulties.map((difficulty) => (
            <FilterChip
              key={difficulty}
              label={difficultyLabel(difficulty)}
              onRemove={() =>
                setFilter({
                  ...filter,
                  difficulties: filter.difficulties.filter((entry) => entry !== difficulty),
                })
              }
            />
          ))}
          {filter.maxTotalMinutes > 0 ? (
            <FilterChip
              label={`bis ${filter.maxTotalMinutes} Min`}
              onRemove={() => setFilter({ ...filter, maxTotalMinutes: 0 })}
            />
          ) : null}
          {filter.maxActiveMinutes > 0 ? (
            <FilterChip
              label={`bis ${filter.maxActiveMinutes} Min aktiv`}
              onRemove={() => setFilter({ ...filter, maxActiveMinutes: 0 })}
            />
          ) : null}
          {filter.minRating > 0 ? (
            <FilterChip
              label={`ab ${filter.minRating} Sternen`}
              onRemove={() => setFilter({ ...filter, minRating: 0 })}
            />
          ) : null}
          {filter.unratedOnly ? (
            <FilterChip label="unbewertet" onRemove={() => setFilter({ ...filter, unratedOnly: false })} />
          ) : null}
          {filter.favoritesOnly ? (
            <FilterChip
              label={texts.favorites.only}
              onRemove={() => setFilter({ ...filter, favoritesOnly: false })}
            />
          ) : null}
          {filter.cookedBefore !== null ? (
            <FilterChip
              label={`seit ${LONG_AGO_DAYS} Tagen nicht gekocht`}
              onRemove={() => setFilter({ ...filter, cookedBefore: null })}
            />
          ) : null}
          <button type="button" onClick={reset} className="text-xs text-accent underline">
            {texts.recipes.clearFilters}
          </button>
        </div>
      ) : null}

      {catalog.error === null ? null : (
        <p className="mb-3 rounded-xl bg-accent-soft px-3 py-2 text-sm text-accent-strong">{catalog.error}</p>
      )}

      {catalog.loading ? (
        <p className="text-sm text-content-muted">{texts.common.loading}</p>
      ) : results.length === 0 ? (
        <p className="rounded-card border border-border-subtle bg-surface-raised p-6 text-center text-sm text-content-muted">
          {catalog.recipes.length === 0
            ? texts.recipes.empty
            : mode === 'favorites' && !hasActiveFilters({ ...filter, favoritesOnly: false })
              ? texts.favorites.empty
              : texts.recipes.emptyFiltered}
        </p>
      ) : (
        <div className={listView ? 'space-y-3' : 'grid gap-3 sm:grid-cols-2 lg:grid-cols-3'}>
          {results.map((recipe: RecipeSummary) => (
            <RecipeCard key={recipe.id} recipe={recipe} categories={catalog.categories} list={listView} />
          ))}
        </div>
      )}

      {filterOpen ? (
        <FilterPanel
          filter={filter}
          categories={catalog.categories}
          tags={catalog.tags}
          resultCount={results.length}
          onApply={(next) => {
            setFilter(next);
            setFilterOpen(false);
          }}
          onClose={() => setFilterOpen(false)}
        />
      ) : null}
    </section>
  );
}
